import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StudySession } from '../../types/Study';
import { examUpdated } from '../exams/examsSlice';
import { UserExam } from '../../types/User';
// import { checkIfStudySessionIsComplete } from '../../utils/studySessionComplete';
import { logout } from '../auth/authSlice';

type TodaySessionStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface TodaySessionState {
  session: StudySession;
  status: TodaySessionStatus;
  error: string | null;
}

const initialSession: StudySession = {
  id: '',
  userId: '',
  date: '',
  flashcardCount: 0,
  completedFlashcardCount: 0,
  vignetteCount: 0,
  blockCount: 0,
  isComplete: false,
  isStarted: false,
  isReview: false,
  isSnoozed: false,
  activeBlockId: '',
  vignetteCorrectCount: 0,
  vignetteIncorrectCount: 0,
  vignetteOmittedCount: 0,
  hasSeenPriorities: false,
  isUsingFlashcards: true,
  isUsingQbank: true,
  isFirstSession: false,
};

const initialState: TodaySessionState = {
  session: initialSession,
  status: 'idle',
  error: null,
};

export const todaySessionSlice = createSlice({
  name: 'todaySession',
  initialState,
  reducers: {
    todaySessionLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    todaySessionReceived: (
      state,
      action: PayloadAction<StudySession | null>
    ) => {
      state.session = action.payload ?? initialSession;
      state.status = 'succeeded';
      state.error = null;
    },
    todaySessionUpdated: (state, action: PayloadAction<StudySession>) => {
      state.session = action.payload;
      state.status = 'succeeded';
      state.error = null;
    },
    todaySessionFailed: (state, action: PayloadAction<string | null>) => {
      state.status = 'failed';
      state.error = action.payload ?? 'Failed to load today session';
    },
    clearTodaySession: (state) => {
      state.session = initialSession;
      state.status = 'idle';
      state.error = null;
    },
    incrCompletedFlashcards: (state) => {
      if (state.session) {
        state.session.completedFlashcardCount += 1;
        // state.session.isComplete = checkIfStudySessionIsComplete(state.session);
      }
    },
    decrCompletedFlashcards: (state) => {
      if (state.session) {
        const newCompletedFlashcardCount = Math.max(
          state.session.completedFlashcardCount - 1,
          0
        );
        state.session.completedFlashcardCount = newCompletedFlashcardCount;
        // state.session.isComplete = checkIfStudySessionIsComplete(state.session);
      }
    },
    updateActiveBlockId: (state, action: PayloadAction<string>) => {
      if (state.session) {
        state.session.activeBlockId = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        examUpdated,
        (state, action: PayloadAction<Partial<UserExam> & { id: string }>) => {
          if (action.payload.primary === true && state.session) {
            if (typeof action.payload.isUsingFlashcards === 'boolean') {
              state.session.isUsingFlashcards =
                action.payload.isUsingFlashcards;
            }
            if (typeof action.payload.isUsingQbank === 'boolean') {
              state.session.isUsingQbank = action.payload.isUsingQbank;
            }
            // state.session.isComplete = checkIfStudySessionIsComplete(
            //   state.session
            // );
          }
        }
      )
      .addCase(logout, (state) => {
        state.session = initialSession;
        state.status = 'idle';
        state.error = null;
      });
  },
});

// Selectors
export const selectTodaySession = (state: {
  todaySession: TodaySessionState;
}) => state.todaySession.session;

export const selectTodaySessionStatus = (state: {
  todaySession: TodaySessionState;
}) => state.todaySession.status;

export const selectTodaySessionError = (state: {
  todaySession: TodaySessionState;
}) => state.todaySession.error;

export const selectActiveBlockId = (state: {
  todaySession: TodaySessionState;
}) => state.todaySession.session?.activeBlockId ?? '';

export const selectIsQBankComplete = createSelector(
  [selectTodaySession],
  (todaySession) => {
    // Ensure we have valid numbers to avoid NaN
    const correctCount = todaySession.vignetteCorrectCount || 0;
    const incorrectCount = todaySession.vignetteIncorrectCount || 0;
    const omittedCount = todaySession.vignetteOmittedCount || 0;
    const totalCount = todaySession.vignetteCount || 0;

    // Avoid division by zero or invalid comparisons
    if (totalCount === 0) return false;

    return correctCount + incorrectCount + omittedCount === totalCount;
  }
);

export const {
  todaySessionLoading,
  todaySessionReceived,
  todaySessionUpdated,
  todaySessionFailed,
  clearTodaySession,
  incrCompletedFlashcards,
  decrCompletedFlashcards,
  updateActiveBlockId,
} = todaySessionSlice.actions;

export default todaySessionSlice.reducer;
